// The app's primitives, built on shadcn/ui (components/shadcn) so every screen
// shares one component set. These keep the dense app API (28 px controls) the
// pages already use; new code may use components/shadcn directly.
import * as React from 'react'
import { Button as SButton } from '@/components/shadcn/button'
import { Input as SInput } from '@/components/shadcn/input'
import { Textarea as STextarea } from '@/components/shadcn/textarea'
import { Label as SLabel } from '@/components/shadcn/label'
import { Card as SCard, CardAction, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Badge as SBadge } from '@/components/shadcn/badge'
import { Kbd as SKbd } from '@/components/shadcn/kbd'
import { Skeleton } from '@/components/shadcn/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/shadcn/toggle-group'
import { Dialog as SDialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/shadcn/dialog'
import { Tooltip as STooltip, TooltipContent, TooltipProvider as STooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/shadcn/dropdown-menu'
import { Table as STable, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { Progress } from '@/components/shadcn/progress'
import { cn } from '@/lib/cn'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
const VARIANT = { primary: 'default', secondary: 'outline', ghost: 'ghost', danger: 'outline' } as const

export const Button = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'sm' | 'md'; title?: string }
>(function Button({ className, variant = 'secondary', size = 'sm', type = 'button', ...props }, ref) {
  return (
    <SButton
      ref={ref}
      type={type}
      variant={VARIANT[variant]}
      size="sm"
      className={cn(
        size === 'sm' ? 'h-7 gap-1.5 px-2.5 text-xs has-[>svg]:px-2' : 'h-8 px-3 text-sm',
        variant === 'secondary' && 'border-border-strong bg-surface-3 shadow-none hover:bg-hover dark:bg-surface-3 dark:hover:bg-hover',
        variant === 'danger' && 'border-border-strong bg-transparent text-destructive shadow-none hover:bg-destructive/10 hover:text-destructive dark:bg-transparent',
        className,
      )}
      {...props}
    />
  )
})

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...props }, ref) {
  return <SInput ref={ref} className={cn('h-8 border-border-strong bg-surface-2 px-2.5 text-sm shadow-none focus-visible:border-accent focus-visible:ring-0 md:text-sm dark:bg-surface-2', className)} {...props} />
})

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...props }, ref) {
  return <STextarea ref={ref} className={cn('min-h-16 px-2.5 py-2 text-sm md:text-sm', className)} {...props} />
})

/** A subtle chevron in place of the browser's own select arrow. */
const CHEVRON = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`

/** shadcn's native select (input styling), kept native for its keyboard and form behaviour. */
export function Select({ className, style, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      data-slot="native-select"
      className={cn('h-8 max-w-full min-w-0 truncate appearance-none rounded-md border border-border-strong bg-surface-2 bg-[length:12px] bg-[right_0.5rem_center] bg-no-repeat pr-7 pl-2 text-xs text-foreground shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-accent disabled:opacity-50', className)}
      style={{ backgroundImage: CHEVRON, ...style }}
      {...props}
    />
  )
}

export function Label({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return <SLabel className={cn('mb-1 text-xs font-normal text-muted-foreground', className)} {...props} />
}

export function Card({ className, title, actions, children }: { className?: string; title?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <SCard className={cn('gap-0 overflow-hidden rounded-lg border-border bg-surface-2 py-0 shadow-none', className)}>
      {(title || actions) && (
        <CardHeader className="flex h-10 items-center px-4 py-0">
          <CardTitle className="text-sm font-medium text-fg">{title}</CardTitle>
          {actions && <CardAction className="flex items-center gap-1 self-center">{actions}</CardAction>}
        </CardHeader>
      )}
      {children}
    </SCard>
  )
}

const TONE = {
  muted: 'text-fg-subtle bg-surface-3',
  ok: 'text-ok bg-ok/10',
  warn: 'text-warn bg-warn/10',
  err: 'text-err bg-err/10',
  info: 'text-info bg-info/10',
  accent: 'text-accent-hover bg-accent-tint',
}

export function Badge({ className, children, tone = 'muted' }: { className?: string; children: React.ReactNode; tone?: keyof typeof TONE }) {
  return <SBadge variant="secondary" className={cn('h-5 rounded-full border-transparent px-2 text-2xs font-medium tabular-nums', TONE[tone], className)}>{children}</SBadge>
}

export function Kbd({ children }: { children: React.ReactNode }) {
  return <SKbd>{children}</SKbd>
}

/** Skeleton rows at real row height: no spinners, no layout jump (03 §8). */
export function SkeletonRows({ rows = 5, height = 28 }: { rows?: number; height?: number }) {
  return (
    <div aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center border-b px-3" style={{ height }}>
          <Skeleton className="h-2.5 rounded-xs bg-surface-3" style={{ width: `${40 + ((i * 17) % 45)}%` }} />
        </div>
      ))}
    </div>
  )
}

export function Segmented<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: React.ReactNode }[]; onChange: (v: T) => void }) {
  return (
    <ToggleGroup type="single" variant="outline" size="sm" value={value} onValueChange={(v) => { if (v) onChange(v as T) }} className="h-7 bg-surface-1">
      {options.map((o) => (
        <ToggleGroupItem key={o.value} value={o.value} className="h-7 px-2 text-xs text-fg-subtle data-[state=on]:bg-surface-4 data-[state=on]:text-fg">
          {o.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  width = 480,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  children: React.ReactNode
  width?: number
}) {
  return (
    <SDialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[12vh] max-h-[76vh] translate-y-0 gap-3 overflow-auto rounded-lg border-border-strong bg-surface-3 p-5 shadow-lg sm:max-w-none" style={{ width, maxWidth: 'calc(100% - 2rem)' }}>
        <DialogHeader className="gap-0.5 pr-6">
          <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
          {description ? <DialogDescription className="text-xs">{description}</DialogDescription> : <DialogDescription className="sr-only">{String(title)}</DialogDescription>}
        </DialogHeader>
        {children}
      </DialogContent>
    </SDialog>
  )
}

export function Tooltip({ content, children }: { content: React.ReactNode; children: React.ReactElement }) {
  if (!content) return children
  return (
    <STooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent sideOffset={4} className="max-w-xs">{content}</TooltipContent>
    </STooltip>
  )
}

export const TooltipProvider = STooltipProvider

export function Menu({ trigger, items }: { trigger: React.ReactElement; items: ({ label: React.ReactNode; onSelect: () => void; danger?: boolean } | 'sep')[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        {items.map((it, i) =>
          it === 'sep'
            ? <DropdownMenuSeparator key={i} />
            : <DropdownMenuItem key={i} onSelect={it.onSelect} variant={it.danger ? 'destructive' : 'default'}>{it.label}</DropdownMenuItem>,
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function Table({ head, children, className }: { head: React.ReactNode[]; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <STable className="text-sm">
        <TableHeader>
          <TableRow className="h-8 border-0 hover:bg-transparent">
            {head.map((h, i) => <TableHead key={i} className="h-9 px-4 text-xs font-normal text-fg-subtle">{h}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>{children}</TableBody>
      </STable>
    </div>
  )
}

export function Tr({ children, onClick, className }: { children: React.ReactNode; onClick?: () => void; className?: string }) {
  return <TableRow onClick={onClick} className={cn('h-9 border-0 hover:bg-hover', onClick && 'cursor-pointer', className)}>{children}</TableRow>
}

export function Td({ children, className, mono }: { children?: React.ReactNode; className?: string; mono?: boolean }) {
  return <TableCell className={cn('px-4 py-0', mono && 'font-mono text-xs', className)}>{children}</TableCell>
}

/**
 * What used to be a second title under the top bar: the top bar already names
 * the page, so only the page's own buttons (and any tabs) are kept here.
 */
export function PageHeader({ actions, children }: { title?: React.ReactNode; meta?: React.ReactNode; actions?: React.ReactNode; children?: React.ReactNode }) {
  if (!actions && !children) return null
  return (
    <div className="px-6">
      {actions && <div className="flex items-center justify-end gap-1.5 py-2">{actions}</div>}
      {children && <div className="flex items-center gap-2 py-2.5">{children}</div>}
    </div>
  )
}

export function Meter({ value, tone = 'info' }: { value: number | null; tone?: 'info' | 'warn' | 'err' | 'ok' }) {
  const v = Math.max(0, Math.min(100, value ?? 0))
  const color = v > 90 ? '*:bg-err' : v > 75 ? '*:bg-warn' : { info: '*:bg-info', warn: '*:bg-warn', err: '*:bg-err', ok: '*:bg-ok' }[tone]
  return <Progress value={v} role="meter" aria-valuenow={v} aria-valuemin={0} aria-valuemax={100} className={cn('h-1.5 w-24 bg-surface-3', color)} />
}

export { Listbox, Switch, type ListboxOption } from './Listbox'
